import { useState, useEffect } from 'react';
import type { ReactElement, ReactNode, SyntheticEvent } from 'react';
import { bisector } from 'd3-array';
import { localPoint } from '@visx/event';
import { Wrapper, DataTableWrapper, Now } from './styled';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import Year from '../Year';
import type { ScaleTime } from 'd3-scale';
import { addYears, format } from 'date-fns';
import { DataTable, Title } from '../DataTable';
import { useTheme } from '@simplywallst/ui-core';

import { VisxText } from '@/components/VisxText';

interface DataTableProps {
  key: number;
}
interface ChildrenProps {
  index: number;
}
export interface NowMarkerProps {
  x: number;
  height: number;
  hover: boolean;
}

interface Props {
  children?: (
    props: ChildrenProps
  ) => ReactElement<ChildrenProps>[] | ReactElement<ChildrenProps> | ReactNode;
  dataTable?: (
    props: DataTableProps
  ) => ReactElement<DataTableProps>[] | ReactElement<DataTableProps>;
  futureLabel?: string;
  height: number;
  keys: number[];
  paddingBottom: number;
  paddingTop: number;
  pastLabel?: string;
  hasTrailingYear?: boolean;
  moveWithTrailingYear?: boolean;
  periods: ReturnType<typeof getTimeSeriesPeriodMap>;
  width: number;
  xScale: ScaleTime<number, number>;
  nowMarker?: (props: NowMarkerProps) => ReactElement<NowMarkerProps>;
}

const bisectDate = bisector((d) => d).left;

const dataTableWidth = 340;

const Markers = ({
  children,
  dataTable,
  futureLabel,
  height,
  keys,
  paddingBottom,
  paddingTop,
  pastLabel,
  periods,
  width,
  xScale,
  hasTrailingYear = true,
  moveWithTrailingYear = false,
  nowMarker,
}: Props) => {
  const theme = useTheme();
  const [index, setIndex] = useState<number>(
    bisectDate(
      keys,
      moveWithTrailingYear ? addYears(periods.NOW, 1) : periods.NOW
    )
  );
  const [hover, setHover] = useState<boolean>(false);
  const nextPeriods = getTimeSeriesPeriodMap(
    new Date(keys[index] ? keys[index] : periods.NOW)
  );
  const x = xScale(nextPeriods.NOW) as number;
  const yearX = xScale(nextPeriods['1Y']) as number;

  useEffect(() => {
    if (keys.length > 0 && index > keys.length - 1) {
      setIndex(keys.length - 1);
    }
  }, [keys, index]);

  const handleMove = (e) => {
    const point = localPoint(e);
    const date = xScale.invert(point ? point.x : 0);
    const nextIndex = bisectDate(
      keys,
      moveWithTrailingYear ? addYears(date, 1) : date
    );
    if (nextIndex !== index && nextIndex <= keys.length - 1) {
      setIndex(nextIndex);
    }
  };

  const handleHover = (e: SyntheticEvent) => {
    if (e.type === 'mouseenter' || e.type === 'touchstart') {
      setHover(true);
    }
    if (e.type === 'mouseleave' || e.type === 'touchend') {
      setHover(false);
    }
  };

  const key = keys[index === null ? -1 : index];

  return (
    <Wrapper>
      <DataTableWrapper
        x={isNaN(x) ? 0 : x}
        width={dataTableWidth}
        flip={x + dataTableWidth > width}
      >
        {dataTable ? (
          dataTable({ key })
        ) : (
          <DataTable>
            <thead>
              <tr>
                <th colSpan={2}>{key ? format(key, 'MMM dd yyyy') : `n/a`}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Title>n/a</Title>
              </tr>
            </tbody>
          </DataTable>
        )}
      </DataTableWrapper>
      <svg width="100%" height={paddingTop + height + paddingBottom}>
        {keys.length > 0 && (
          <>
            {nowMarker ? (
              nowMarker({ x, height, hover })
            ) : (
              <Now
                y="0"
                x={x}
                width="1"
                height={24 + height}
                fill={theme.color.brand01}
                hover={hover}
              />
            )}
            {hasTrailingYear && (
              <Year y={24} x={x} yearX={yearX} height={height} />
            )}
            {children &&
              children({
                index,
              })}
            {pastLabel && (
              <VisxText
                x={xScale(periods.NOW)}
                y={paddingTop}
                dx="-8"
                dy="8"
                textAnchor="end"
                verticalAnchor="start"
                fill={theme.color.white}
                typography="tiny"
              >
                {pastLabel}
              </VisxText>
            )}
            {futureLabel && (
              <VisxText
                x={xScale(periods.NOW)}
                y={paddingTop}
                dx="8"
                dy="8"
                textAnchor="start"
                verticalAnchor="start"
                fill={theme.color.whiteRGBA(0.5)}
                typography="tiny"
              >
                {futureLabel}
              </VisxText>
            )}
            <rect
              width="100%"
              height="100%"
              fill="transparent"
              onMouseMove={handleMove}
              onMouseEnter={handleHover}
              onMouseLeave={handleHover}
              onTouchMove={handleMove}
              onTouchStart={handleHover}
              onTouchEnd={handleHover}
            />
          </>
        )}
      </svg>
    </Wrapper>
  );
};

export default Markers;
